import React from 'react';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import PropTypes from 'prop-types';
import { createTheme, ThemeProvider } from '@mui/material';
// v7 removed the re-export of StyledEngineProvider from '@mui/material' — only
// '@mui/material/styles' provides it now.
import { StyledEngineProvider } from '@mui/material/styles';
import { Global } from '@emotion/react';
import * as colors from '@constants/colors';
import LanguageProvider from '@components/language-provider';
import ConfigurationLoader from '@components/config/loader';
import RoutesWrapper from '../routes';
import { PickersUtilsProvider, Snackbar } from '@mui';

const materialTheme = createTheme({
  palette: {
    primary: {
      main: colors.secondaryColor
    },
    // v5 changed the default secondary color from pink (v4's pink.A400, #f50057) to
    // purple (purple[500]) — restored explicitly since this theme never overrode it and
    // several places (e.g. the discard confirmation button) rely on the old default.
    secondary: {
      main: '#f50057'
    }
  },
  components: {
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.dotmapsBlue
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 0.7
          }
        },
        track: {
          backgroundColor: '#767676',
          opacity: 0.5
        }
      }
    },
    MuiPickersDay: {
      styleOverrides: {
        // Keep most day override styles commented,
        // the default ones are better.
        // v6 stabilized MuiPickersDay's internal state classes and now warns (real error,
        // not just a lint nit) if `selected`/`disabled`/`today` are used as separate top-level
        // style keys, since that no longer has enough CSS specificity to win over the
        // component's own internal state styles — nest them under `root` via `&.Mui-x` instead.
        root: {
          // color: colors.dotmapsGray
          '&.Mui-selected': {
            backgroundColor: colors.dotmapsBlue
          },
          '&.Mui-disabled': {
            // color: colors.dotmapsBlack40
          },
          '&.MuiPickersDay-today': {
            // color: colors.dotmapsBlue
          }
        }
      }
    },
    // Moved out of GlobalCss's `@global` block: that rule and MUI's own built-in
    // `.MuiInputBase-input` font-size are the same specificity, so whichever one's CSS
    // happens to be inserted into the page later wins — non-deterministic between the local
    // dev bundle and production's, producing a real 14px-vs-16px input text size difference
    // between environments. Theme-level styleOverrides are guaranteed to win over a
    // component's own base styles regardless of insertion order, so this is deterministic.
    // Set to 1rem (16px) to match what's actually live on production today, not the old
    // 0.875rem (14px) override that production's own build has been losing this race against.
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: colors.dotmapsBlack80,
          fontSize: '1rem'
        }
      }
    },
    // v5 changed FormLabel's default lineHeight from v4's unitless `1` to `1.4375em` — since
    // our own GlobalCss forces this label to 0.875rem (14px), that em-based line-height now
    // computes to 20.125px instead of just 14px, making every shrunk field label render ~5px
    // taller (and, combined with the 0.85 shrink-scale transform, visibly taller/more padded)
    // than production. Restored to v4's unitless 1 so it always equals the label's own font-size.
    //
    // color/fontSize moved here from the old global `.MuiFormLabel-root` CSS rule below — that
    // rule and MUI's own built-in FormLabel styles are the same specificity, so whichever one's
    // CSS is inserted into the page later wins (same non-deterministic race already documented
    // on MuiInputBase above). Adding the date/time picker's PickersTextField at v9 shifted the
    // insertion order enough that the global rule started losing specifically for picker labels,
    // which rendered at MUI's own default 16px instead of the intended 14px. Theme-level
    // styleOverrides always win regardless of insertion order, so this is deterministic.
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colors.dotmapsBlack60,
          fontSize: '0.875rem',
          lineHeight: 1
        }
      }
    },
    // Same non-deterministic global-vs-component-default race as MuiFormLabel/MuiInputBase
    // above, moved here for the same reason: the old global `.MuiInputLabel-shrink` rule was
    // losing against MUI's own default shrink transform (scale(0.75)) instead of ours
    // (scale(0.85)), making every shrunk label render smaller than production.
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          color: colors.dotmapsBlack60,
          transform: 'translate(0, 1.5px) scale(0.85)'
        }
      }
    }
  }
});

// tss-react (our @mui/styles replacement, dropped in favor of it since @mui/styles has no
// working release compatible with a v7-created theme) has no equivalent to JSS's '@global'
// plugin — global, unscoped CSS overrides now go through @emotion/react's own <Global>
// component instead, which composes into the same emotion cache/insertion-order as
// everything else here (still respects injectFirst below).
const globalStyles = {
  '.MuiTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root': {  // TextField icon.
    // v5 added a -12px right margin to edge="end" IconButtons (to keep the icon flush
    // against the edge despite v5's own larger default padding) — with padding forced
    // to 0 here, that margin now overhangs the field instead of being flush; v4 had
    // neither, so neutralize it.
    margin: '0',
    padding: '0'
  },
  // Autocomplete's own built-in styles (@mui/material/Autocomplete/Autocomplete.js) set this
  // exact compound selector's padding to '2px 4px 3px 0' for size="small" inputs — higher
  // specificity than our TextField wrapper's classes.input override, so it always won. v4's
  // equivalent (margin="dense") was '3px 0 6px' (matches production); matching MUI's own
  // selector shape here so ours wins the specificity tie (JSS injects after emotion/MUI's
  // own styles, per injectFirst in this file).
  '.MuiAutocomplete-root .MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input': {
    padding: '3px 0 6px'
  },
  '.MuiAutocomplete-option': {
    boxShadow: 'inset 0 -1px 0 0 #EBEBEB'
  },
  // Customize the Avatar's initials to show them in white instead of
  // the default grey color.
  '.MuiChip-root .MuiChip-avatar': {
    color: 'white'
  }
  // This commented code below might be used when we move
  // to use the Dialog components from the new Material UI version.
  /*
  '.MuiDialogActions-root.withAdditionalAction': {
    // Set justifyContent to default value to fix IE11 layout bug
    // see https://github.com/mui-org/material-ui-pickers/pull/267
    justifyContent: 'flex-start',
    // Make the first action button to be aligned
    // to the left (by adding some margin to the right,
    // when there's an additional action).
    '& > *:first-child': {
      marginRight: 'auto'
    }
  },
  '.MuiDialogActions-root': {
    marginTop: '1rem'  // Add some padding to the dialog actions.
  }
  */
};

const Root = ({ history, messages, store }) => (
  <StyledEngineProvider injectFirst>
    <PickersUtilsProvider>
      <Global styles={globalStyles} />
      <Provider store={store}>
        <LanguageProvider messages={messages}>
          <ThemeProvider theme={materialTheme}>
            <Snackbar.Provider />
            <ConnectedRouter history={history}>
              <ConfigurationLoader>
                <RoutesWrapper />
              </ConfigurationLoader>
            </ConnectedRouter>
          </ThemeProvider>
        </LanguageProvider>
      </Provider>
    </PickersUtilsProvider>
  </StyledEngineProvider>
);

Root.propTypes = {
  history: PropTypes.object.isRequired,
  messages: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired
};

export default Root;
